import React, { useState } from 'react';
import styled from 'styled-components';
import { gql, useQuery, useApolloClient } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import {
  ProgrammeChoiceForm,
  checkIsPartnerPartShown,
} from './ProgrammeChoiceForm';
import { PAGE_TEXT, PROGRAMME_TYPE } from '..';
import {
  addApplicationItem,
  type ProgrammeChoiceFormData,
  useCreateApplication,
  useGetChoiceData,
  updateApplicationItem,
  CONFLICT_APPLICATION,
} from '../../../graphql';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Loading,
  type SelectOption,
  Dialog,
} from '../../../../../components';
import { useAuth } from '../../../../../contexts';
import { throttle, APPLICATION_STATUS_TYPE } from '../../../../../utils';

const GET_APPLICANT = gql`
  query GetApplicant($email: String) {
    applicants(filter: { email: { eq: $email } }) {
      _id
    }
  }
`;

export type ProgrammeChoiceContentProps = {
  programmeChoice: ProgrammeChoiceFormData;
  enableEdit: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const ProgrammeChoice: React.FunctionComponent<
  ProgrammeChoiceContentProps
> = props => {
  const { PROGRAMME, PROGRAMME_CHOICE } = PAGE_TEXT;
  const {
    programmeChoice: initialProgrammeChoice,
    enableEdit,
    refetchApplication,
    handleLoading,
  } = props;
  const { user } = useAuth();
  const location = useParams();
  const navigate = useNavigate();
  const client = useApolloClient();
  let hasConflict: null | string = null; // null or string
  const [applicationId, setApplicationId] = useState('');
  const [openCreationDialog, setOpenCreationDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const studentEmail = user?.userInfo?.username ?? '';

  const {
    loading: getApplicantLoading,
    error: getApplicantError,
    data = {},
  } = useQuery(GET_APPLICANT, {
    variables: { email: studentEmail },
    skip: !studentEmail,
  });

  const { applicants = [] } = data;
  const applicantId = applicants?.[0]?._id ?? '';

  const [programmeChoiceId, setProgrammeChoiceId] = useState(
    initialProgrammeChoice.programmeChoiceId ?? '',
  );

  // get entryYear, department, rpgJoinPhdPartnerList, prgCollabPhdPartnerList from backend setting
  const {
    programmeChoiceMetadata,
    programmeChoiceEntryYears,
    loading: choiceMetaLoading,
    error: choiceMetaError,
  } = useGetChoiceData();

  const { departments, rpgJoinPhdPartnerList, prgCollabPhdPartnerList } =
    programmeChoiceMetadata;

  const filteredEntryYears = programmeChoiceEntryYears.filter(
    item =>
      item.shouldShow === true ||
      initialProgrammeChoice.entryYear === item.value,
  );

  const entryYearsOptions: SelectOption[] = filteredEntryYears.map(item => ({
    value: item.value,
    label: item.label,
    disabled: item.shouldShow === false,
  }));

  const departmentOptions: SelectOption[] = (departments as any[]).map(
    department => ({
      value: department.value,
      label: department.label,
    }),
  );

  const hasProgrammeChoice = Boolean(programmeChoiceId);

  const createProgrammeChoice = addApplicationItem(
    'programmeChoice',
    refetchApplication,
  );

  const updateProgrammeChoice = updateApplicationItem();
  const createApplication = useCreateApplication();

  const redirectToApplicationCreation = async (): Promise<
    string | undefined
  > => {
    try {
      const res = await createApplication({
        variables: {
          applicantId,
          status: APPLICATION_STATUS_TYPE.CREATED,
        },
      });
      const applicationId: string = res.data?.createApplication?._id ?? '';
      setApplicationId(applicationId);
      return applicationId;
    } catch (e: any) {
      setAlertDetails({
        alertContent: `Error occurs.${(e as Error)?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
      return ''; // the return value will influence whether will create programme choice
    }
  };

  const handleOpenApplication = (): void => {
    navigate(`/application/${applicationId}`);
  };

  const handleCloseCreationDialog = (): void => {
    setOpenCreationDialog(false);
  };

  const checkConflict = async (
    values: ProgrammeChoiceFormData,
  ): Promise<void> => {
    const { department, entryYear, programme } = values;
    await handleLoading(async () => {
      try {
        const { data } = await client.query({
          query: CONFLICT_APPLICATION,
          variables: {
            department,
            programme,
            entryYear,
          },
        });

        const { hasConflictApplication } = data;
        hasConflict = hasConflictApplication; // null or applicationId
      } catch (error: any) {
        setAlertDetails({
          alertContent: `Error occurs.${error?.message}`,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    });
  };

  const handleSubmit = throttle(
    async (values: ProgrammeChoiceFormData): Promise<void> => {
      /** 1. check the programme choice */
      await checkConflict(values);

      if (!hasProgrammeChoice) {
        if (typeof hasConflict === 'string') {
          setAlertDetails({
            alertContent: PROGRAMME_CHOICE.ALERT_TEXT.CONFLICT,
            alertType: 'warning',
          });
          setOpenAlert(true);
          setCreateLoading(false);

          return;
        }
      }
      const {
        partner,
        isDualOrCollaborative,
        programmeType,
        proposedChiefSupervisor,
        proposedChiefSupervisorBackup = '',
        programmeChoiceId,
        ...others
      } = values;
      const { universityCategory, ...partnerOthers } = partner;
      const programmeChoice = {
        ...others,
        partner: checkIsPartnerPartShown(values.programmeType)
          ? {
              ...partnerOthers,
              universityCategory:
                programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME
                  ? universityCategory
                  : null,
            }
          : null,
        programmeType: isDualOrCollaborative
          ? programmeType
          : PROGRAMME_TYPE.GENERAL_PROGRAMME,
        // should not pass the *Not Indicated* to backend
        proposedChiefSupervisor:
          proposedChiefSupervisor === PROGRAMME_CHOICE.OPTIONS.NO_INDICATION
            ? null
            : proposedChiefSupervisor,

        proposedChiefSupervisorBackup:
          proposedChiefSupervisorBackup === ''
            ? null
            : proposedChiefSupervisorBackup,
      };

      await handleLoading(async () => {
        try {
          if (!hasProgrammeChoice) {
            /* 2. check if valid user first for creating a new application */
            const invalidApplicant = !(applicantId as boolean);
            if (invalidApplicant) {
              setAlertDetails({
                alertContent: PROGRAMME_CHOICE.ALERT_TEXT.INVALID_USER,
                alertType: 'warning',
              });
              setOpenAlert(true);
              return;
            }

            /** 3. create the Application */
            const applicationId = await redirectToApplicationCreation();
            const validApplication = Boolean(applicationId);
            /** 4. create the ProgrammeChoice */
            if (validApplication) {
              const res = await createProgrammeChoice({
                variables: {
                  id: applicationId,
                  input: {
                    programmeChoice,
                  },
                },
              });
              const resId = res.data?.addApplicationItem?.programmeChoice?._id;
              setProgrammeChoiceId(resId);
              setOpenCreationDialog(true);
            } else {
              /** stop creating prog choice if create application failed */
              throw Error('Fail to create application.');
            }
          } else {
            /** update mode */
            if (
              hasConflict !== null &&
              location.applicationId !== hasConflict
            ) {
              setAlertDetails({
                alertContent: PROGRAMME_CHOICE.ALERT_TEXT.CONFLICT,
                alertType: 'warning',
              });
              setOpenAlert(true);
              setCreateLoading(false);
              return;
            }
            await updateProgrammeChoice({
              variables: {
                id: location.applicationId,
                updateId: programmeChoiceId,
                input: {
                  programmeChoice,
                },
              },
            });
            setAlertDetails({
              alertContent: PROGRAMME_CHOICE.ALERT_TEXT.SAVE_SUCCEED,
              alertType: 'success',
            });
            setOpenAlert(true);
          }

          refetchApplication();
        } catch (error: any) {
          setAlertDetails({
            alertContent: `${PROGRAMME_CHOICE.ALERT_TEXT.SAVE_FAILED} ${error?.message}`,
            alertType: 'error',
          });
          setOpenAlert(true);
        }
      });
    },
    1500,
  );

  const formProps = {
    initialValues: initialProgrammeChoice,
    enableEdit,
    onSubmit: handleSubmit,
    options: {
      entryYearsOptions,
      departmentOptions,
      rpgJoinPhdPartnerList,
      prgCollabPhdPartnerList,
    },
  };

  if (getApplicantError ?? choiceMetaError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (getApplicantError as Error)?.message +
            (choiceMetaError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const loading = getApplicantLoading || choiceMetaLoading;

  return (
    <Container>
      {createLoading && <Loading />}
      <Dialog
        openDialog={openCreationDialog}
        dialogTitle={''}
        dialogAction={PAGE_TEXT.CONFIRM}
        onDialogAction={handleOpenApplication}
        onDialogClose={handleCloseCreationDialog}
      >
        <Typography margin={'1rem 2rem'}>
          {PAGE_TEXT.CREATE_APPLICATION_SUCCESS}
        </Typography>
      </Dialog>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <ContentBox>
          <Accordion
            defaultExpanded
            title={PROGRAMME.TITLE}
            details={<ProgrammeChoiceForm {...formProps} />}
          />
        </ContentBox>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
